/**
 * PCM播放引擎
 *
 * 使用AudioContext播放PCM
 */

import {
    AUDIO_CONFIG
} from "../constants/TTSConstants";
import IOSSilentUnlock from "./IOSSilentUnlock";


export default class PCMEngine {
    constructor() {
        this.context = null;
        this.queue = [];
        this.playing = false;
        this.currentSource = null;
        this.playVersion = 0;
        this.hasStarted = false;
        this.onDrained = null;
        // iOS 静音开关兼容：尽早注册手势监听，抢在用户第一次点击时解锁
        IOSSilentUnlock.ensureUnlocked();
    }

    ensureContext() {
        if (!this.context || this.context.state === "closed") {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            this.context = new AudioCtx();
            IOSSilentUnlock.ensureUnlocked();
        }
    }

    /**
     * 添加PCM数据
     *
     * @param buffer ArrayBuffer
     */
    push(buffer) {
        this.queue.push(buffer);
        this.playNext();
    }

    /**
     * 播放下一段
     */
    async playNext() {
        if (this.playing) {
            return;
        }
        // iOS 静音开关兼容：播放前确保媒体播放会话已激活
        IOSSilentUnlock.ensureUnlocked();
        const pcm = this.shiftMergedBuffer();

        if (!pcm) {
            // 播放过且队列空了 → 真正播放完毕
            if (this.hasStarted) {
                this.hasStarted = false;
                this.onDrained?.();
            }
            return;
        }

        const version = this.playVersion;
        this.ensureContext();
        if (this.context.state === "suspended") {
            await this.context.resume();
        }

        if (version !== this.playVersion) {
            return;
        }

        this.playing = true;
        this.hasStarted = true;
        const audioBuffer = this.createAudioBuffer(pcm);

        const source = this.context.createBufferSource();
        this.currentSource = source;
        source.buffer = audioBuffer;
        source.connect(this.context.destination);

        source.onended = () => {
            if (this.currentSource !== source || version !== this.playVersion) {
                return;
            }
            this.currentSource = null;
            this.playing = false;
            this.playNext();
        };
        source.start();
    }

    shiftMergedBuffer() {
        if (!this.queue.length) {
            return null;
        }

        if (this.queue.length === 1) {
            return this.queue.shift();
        }

        const totalLength = this.queue.reduce((sum, buffer) => sum + buffer.byteLength, 0);
        const merged = new Uint8Array(totalLength);
        let offset = 0;

        this.queue.forEach(buffer => {
            merged.set(new Uint8Array(buffer), offset);
            offset += buffer.byteLength;
        });

        this.queue = [];
        return merged.buffer;
    }

    createAudioBuffer(buffer) {
        const wav = this.parseWav(buffer);

        if (wav) {
            return this.createPcmAudioBuffer(
                wav.data,
                wav.sampleRate,
                wav.channels,
                wav.bitsPerSample
            );
        }

        return this.createPcmAudioBuffer(
            buffer,
            AUDIO_CONFIG.SAMPLE_RATE,
            AUDIO_CONFIG.CHANNELS,
            AUDIO_CONFIG.BIT_DEPTH
        );
    }

    createPcmAudioBuffer(buffer, sampleRate, channels, bitsPerSample) {
        if (bitsPerSample !== 16) {
            throw new Error(`[PCMEngine] unsupported bit depth: ${bitsPerSample}`);
        }

        const samples = new Int16Array(buffer, 0, Math.floor(buffer.byteLength / 2));
        const frameCount = Math.floor(samples.length / channels);
        const audioBuffer = this.context.createBuffer(channels, frameCount, sampleRate);

        for (let channelIndex = 0; channelIndex < channels; channelIndex++) {
            const channelData = audioBuffer.getChannelData(channelIndex);

            for (let i = 0; i < frameCount; i++) {
                channelData[i] = samples[i * channels + channelIndex] / 32768;
            }
        }

        return audioBuffer;
    }

    parseWav(buffer) {
        if (buffer.byteLength < 44) {
            return null;
        }

        const view = new DataView(buffer);

        if (
            this.readString(view, 0, 4) !== "RIFF" ||
            this.readString(view, 8, 4) !== "WAVE"
        ) {
            return null;
        }

        let offset = 12;
        let sampleRate = AUDIO_CONFIG.SAMPLE_RATE;
        let channels = AUDIO_CONFIG.CHANNELS;
        let bitsPerSample = AUDIO_CONFIG.BIT_DEPTH;
        let dataOffset = -1;
        let dataSize = 0;

        while (offset + 8 <= buffer.byteLength) {
            const chunkId = this.readString(view, offset, 4);
            const chunkSize = view.getUint32(offset + 4, true);
            const chunkDataOffset = offset + 8;

            if (chunkId === "fmt ") {
                channels = view.getUint16(chunkDataOffset + 2, true);
                sampleRate = view.getUint32(chunkDataOffset + 4, true);
                bitsPerSample = view.getUint16(chunkDataOffset + 14, true);
            }

            if (chunkId === "data") {
                dataOffset = chunkDataOffset;
                dataSize = chunkSize;
                break;
            }

            offset = chunkDataOffset + chunkSize + (chunkSize % 2);
        }

        if (dataOffset < 0) {
            return null;
        }

        return {
            sampleRate,
            channels,
            bitsPerSample,
            data: buffer.slice(dataOffset, dataOffset + dataSize)
        };
    }

    readString(view, offset, length) {
        let value = "";

        for (let i = 0; i < length; i++) {
            value += String.fromCharCode(view.getUint8(offset + i));
        }

        return value;
    }

    /**
     * 恢复AudioContext
     */
    resume() {
        // iOS 静音开关兼容：恢复前确保媒体播放会话已激活
        IOSSilentUnlock.ensureUnlocked();
        this.ensureContext();
        if (this.context.state === "suspended") {
            this.context.resume();
        }
    }

    /**
     * 停止
     */
    stop() {
        this.playVersion++;
        this.queue = [];
        this.playing = false;
        this.hasStarted = false;

        if (this.currentSource) {
            const source = this.currentSource;
            this.currentSource = null;
            source.onended = null;
            try {
                source.stop();
            } catch (e) {
                console.warn("[PCMEngine] source already stopped", e);
            }
        }

        if (this.context && this.context.state === "running") {
            this.context.suspend();
        }
    }

    /**
     * 关闭
     */
    close() {
        this.stop();
        if (this.context && this.context.state !== "closed") {
            this.context.close();
        }
    }

}
